/**
 * 页面追踪模块
 * 页面打开自动上报 openView + SPA 路由监听（popstate + pushState/replaceState）+ 页面销毁上报
 */

import type { TrackData, EventData, CommonParams, RoutePathResolver, ReferrerResolver, PageReportMode, PageParams } from '../types'
import { collectPageEvent, collectPageLeaveEvent, resolveRoutePath, getPageId, collectPageParams, updateSpaLastPageUrl } from '../collectors/page'
import { refreshDynamicFields } from '../collectors/common'
import { Reporter } from '../reporter'
import type { DynamicConfigManager } from '../core/dynamic-config'
import type { NetworkModule } from './network'
import type { RouteChangeManager, RouteChangeInfo, Unsubscribe } from '../core/route-change-manager'
import { LeaveType } from '../types/event'
import { extractAttributes, collectMissingFields, sleep } from '../utils'

export interface PageModuleConfig {
  enabled: boolean
  debug?: boolean
  routePathResolver?: RoutePathResolver
  referrerResolver?: ReferrerResolver
  pageReportMode?: PageReportMode
}

// 页面打开上报延时时间（ms），等待 DOM 渲染和接口响应
const PAGE_VIEW_REPORT_DELAY_MS = 2000

/**
 * 页面追踪模块
 */
export class PageModule {
  /** 模块配置 */
  private config: PageModuleConfig
  /** 报告器 */
  private reporter: Reporter
  /** 全局公共参数 */
  private commonParams: CommonParams
  /** 动态配置管理器 */
  private dynamicConfigManager?: DynamicConfigManager
  /** 网络模块 */
  private networkModule?: NetworkModule
  /** 页面进入时间 */
  private pageEnterTime: number = 0
  /** 当前页面路由路径 */
  private currentRoutePath: string = ''
  /** 当前页面 ID */
  private currentPageId: string = ''
  /** 当前页面 URL 快照（进入时记录，避免路由切换后 location.href 已变） */
  private currentPageUrl: string = ''
  /** 当前页面级参数 */
  private currentPageParams: PageParams | null = null
  /** 是否已初始化 */
  private isInitialized = false

  /** 路由变更管理器 */
  private routeChangeManager: RouteChangeManager
  /** 路由订阅取消函数 */
  private unsubscribeRouteChange: Unsubscribe | null = null
  /** 页面离开是否已上报（防止 beforeunload + pagehide 重复触发） */
  private hasPageLeaveReported = false
  /** 延时上报页面打开定时器 */
  private pageViewTimer: ReturnType<typeof setTimeout> | null = null
  /** 当前页面的 openView 是否已上报（防重复） */
  private hasPageViewReported = false

  constructor(
    config: PageModuleConfig,
    reporter: Reporter,
    commonParams: CommonParams,
    routeChangeManager: RouteChangeManager,
    dynamicConfigManager?: DynamicConfigManager,
    networkModule?: NetworkModule
  ) {
    this.config = config
    this.reporter = reporter
    this.commonParams = commonParams
    this.routeChangeManager = routeChangeManager
    this.dynamicConfigManager = dynamicConfigManager
    this.networkModule = networkModule
  }

  /**
   * 初始化模块
   */
  init(): void {
    if (!this.config.enabled || this.isInitialized) return
    if (typeof window === 'undefined') return

    this.isInitialized = true
    this.pageEnterTime = Date.now()
    this.currentRoutePath = resolveRoutePath(this.config.routePathResolver)
    this.currentPageId = getPageId(this.currentRoutePath)
    this.currentPageUrl = typeof location !== 'undefined' ? location.href : ''

    // 初始化时记录当前 URL，供后续路由变化时作为上一页面 URL
    updateSpaLastPageUrl('')

    // 调度延时上报页面打开事件
    this.schedulePageViewReport()

    // 订阅路由变化
    this.unsubscribeRouteChange = this.routeChangeManager.subscribe(this.handleRouteChange)

    // 监听页面卸载
    this.setupUnloadListener()

    // this.log('Page module initialized')
  }

  /**
   * 更新公共参数引用
   */
  updateCommonParams(commonParams: CommonParams): void {
    this.commonParams = commonParams
  }

  /**
   * 设置页面卸载监听
   */
  private setupUnloadListener(): void {
    window.addEventListener('beforeunload', this.handlePageLeave)
    window.addEventListener('pagehide', this.handlePageLeave)

    // 监听页面隐藏（移动端切换 Tab）
    // 暂不监听
    // document.addEventListener('visibilitychange', () => {
    //   if (document.visibilityState === 'hidden') {
    //     this.handlePageLeave()
    //   }
    // })
  }

  /**
   * 处理路由变化
   */
  private handleRouteChange = async (info: RouteChangeInfo): Promise<void> => {
    // 重置页面离开上报标志（路由变化是新页面，可以重新上报）
    this.hasPageLeaveReported = false

    // 冲刷旧页面尚未上报的 openView
    this.flushPageViewReport()
    // 确保上报顺序（先冲刷旧页面 openView、再上报旧页面 closeView）
    await sleep(0)
    // 上报离开事件（使用旧的 routePath）
    this.reportPageLeave('route_change', info.newUrl, info.oldUrl)

    // 更新 SPA 上一页面 URL（在采集新页面事件之前）
    updateSpaLastPageUrl(info.oldUrl)

    // 确保上报顺序（先旧页面 closeView、再新页面 openView）
    await sleep(0)

    // 更新状态
    this.pageEnterTime = Date.now()
    this.currentRoutePath = resolveRoutePath(this.config.routePathResolver)
    this.currentPageId = getPageId(this.currentRoutePath)
    this.currentPageUrl = typeof location !== 'undefined' ? location.href : ''

    // 为新页面启动延时上报
    this.schedulePageViewReport()
  }

  /**
   * 处理页面离开
   */
  private handlePageLeave = (): void => {
    // 防止 beforeunload 和 pagehide 重复触发
    if (this.hasPageLeaveReported) return
    this.hasPageLeaveReported = true

    // 冲刷尚未上报的 openView
    this.flushPageViewReport()

    this.reportPageLeave('unload')
  }

  /**
   * 获取当前页的请求记录
   */
  getCurrentPageRequests(): any[] {
    return this.networkModule?.getCurrentPageRequests() ?? []
  }

  /**
   * 根据 pageReportMode 判断是否应跳过页面上报
   */
  private shouldSkipPageReport(pageId: string): boolean {
    const mode = this.config.pageReportMode

    if (mode === 'full') {
      return false
    }

    if (mode === 'configOnly') {
      if (!this.dynamicConfigManager) {
        return true
      }
      return !this.dynamicConfigManager.isPageConfigured(pageId)
    }

    // undefined: 保持现有行为
    if (this.dynamicConfigManager) {
      return !this.dynamicConfigManager.isPageConfigured(pageId)
    }
    return false
  }

  /**
   * 上报页面打开事件
   * @param 扩展参数（可选）
   */
  reportPageView(extParams?: { common?: CommonParams; event?: EventData; business?: any }): TrackData | null {
    if (this.shouldSkipPageReport(this.currentPageId)) {
      this.log('Page report skipped', { pageId: this.currentPageId, mode: this.config.pageReportMode })
      return null
    }

    const pageEvent = collectPageEvent(this.currentRoutePath, this.config.referrerResolver)
    const pageParams = this.getPageParams()
    // 当extParams.common存在时，使用传入的common
    const common = extParams?.common ? { ...extParams.common } : refreshDynamicFields(this.commonParams)
    const networkRecords = this.networkModule?.getCurrentPageRequests() ?? []
    // 根据 pageId 获取当前页的配置
    const pageConfig = this.dynamicConfigManager?.getPageConfig(this.currentPageId)
    const event: EventData = {
      eventType: 'openView',
      eventName: pageConfig?.eventMap?.openView?.eventName || '',
      eventCode: pageConfig?.eventMap?.openView?.eventCode || '',
      page: {
        referrerPage: pageEvent.referrerPage,
        currentUrl: this.currentPageUrl,
        pageName: pageConfig?.pageName || pageEvent?.pageTitle || '学天教育',
        pageId: pageEvent.pageId,
      },
    }

    // 提取attributes参数
    const attributesData = extractAttributes(pageConfig?.eventMap?.openView.fields || [], {
      pageParams: pageParams || {},
      businessParams: {},
      apis: networkRecords
    })

    // 收集 fieldList 中 value 为 null 或 undefined 的字段信息
    const missingFields = collectMissingFields(attributesData.fieldList)

    const data: TrackData = {
      common,
      event,
      business: {
        attributes: attributesData,
        missingFields,
        eventResult: {},
      }
    }

    this.reporter.report(data)
    
    this.log('Page 打开已上报', data)
    return data
  }

  /**
   * 上报页面离开事件
   */
  private reportPageLeave(leaveType: LeaveType, targetPage?: string, currentUrl?: string): void {
    if (this.shouldSkipPageReport(this.currentPageId)) {
      this.log('Page leave report skipped', { pageId: this.currentPageId, mode: this.config.pageReportMode })
      return
    }

    // 根据 pageId 获取当前页的配置
    const pageConfig = this.dynamicConfigManager?.getPageConfig(this.currentPageId)
    const pageParams = collectPageLeaveEvent(
      this.pageEnterTime,
      leaveType,
      targetPage,
      currentUrl,
      true,
      this.currentRoutePath,
      this.config.referrerResolver
    )
    const common = refreshDynamicFields(this.commonParams)
    const event: EventData = {
      eventType: 'closeView',
      eventName: pageConfig?.eventMap?.closeView?.eventName || '',
      eventCode: pageConfig?.eventMap?.closeView?.eventCode || '',
      page: {
        referrerPage: pageParams.referrerPage, // 当前页的
        currentUrl: pageParams.currentUrl || pageParams.referrerPage || '', // 当前页的，由于路由已变化，所以取referrerPage
        pageName: pageConfig?.pageName || pageParams.pageTitle || '学天教育', // 当前页的
        pageId: pageParams.pageId, // 当前页的
        stayDuration: pageParams.stayDuration, // 当前页的
        leaveType: pageParams.leaveType, // 当前页的
        targetPage: pageParams.targetPage, // 下一页的
      },
    }

    const data: TrackData = {
      common,
      event,
      business: {
        attributes: {},
        missingFields: [],
        eventResult: {},
      },
    }

    this.reporter.report(data)
    this.log('Page leave reported', pageParams)
  }

  /**
   * 销毁模块
   */
  destroy(): void {
    if (!this.isInitialized) return

    // 取消延时上报定时器
    this.cancelPageViewTimer()

    // 取消路由变化订阅
    this.unsubscribeRouteChange?.()
    this.unsubscribeRouteChange = null

    // 移除页面卸载事件监听
    window.removeEventListener('beforeunload', this.handlePageLeave)
    window.removeEventListener('pagehide', this.handlePageLeave)

    this.isInitialized = false
    this.currentPageParams = null
    this.hasPageLeaveReported = false
    this.log('Page module destroyed')
  }

  /**
   * 采集页面级参数并打印日志
   */
  private collectAndLogPageParams(): void {
    this.currentPageParams = collectPageParams(this.config.routePathResolver)
    this.log('Page 参数已采集', this.currentPageParams)
  }

  /**
   * 获取当前页面级参数
   * @returns PageParams 的浅拷贝，未初始化时返回 null
   */
  getPageParams(): PageParams | null {
    if (!this.currentPageParams) return null
    return {
      query: { ...this.currentPageParams.query },
      params: { ...this.currentPageParams.params },
      custom: { ...this.currentPageParams.custom },
    }
  }

  /**
   * 调试
   */
  private log(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.log('%c[Tracker Page 调试] %c ' + message, 'background: #a9d134; color: #fff;', 'color: inherit;', data)
    }
  }

  /**
   * 调度延时上报页面打开事件
   * 启动定时器，到时间后采集数据并上报；若提前离开则由 flushPageViewReport 冲刷
   */
  private schedulePageViewReport(): void {
    this.cancelPageViewTimer()
    this.hasPageViewReported = false
    this.pageViewTimer = setTimeout(() => {
      this.pageViewTimer = null
      this.flushPageViewReport()
    }, PAGE_VIEW_REPORT_DELAY_MS)
  }

  /**
   * 立即冲刷页面打开上报
   * 取消定时器，采集当前已有数据并上报（若尚未上报）
   */
  private flushPageViewReport(): void {
    this.cancelPageViewTimer()
    if (this.hasPageViewReported) return
    this.hasPageViewReported = true
    // 上报前重新采集页面级参数（取最新的 DOM 数据）
    this.collectAndLogPageParams()
    this.reportPageView()
  }

  /**
   * 取消延时上报定时器
   */
  private cancelPageViewTimer(): void {
    if (this.pageViewTimer !== null) {
      clearTimeout(this.pageViewTimer)
      this.pageViewTimer = null
    }
  }
}
